// Login.tsx
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import React, { useState } from 'react';
import { AuthLayout } from '@/components/layouts/AuthLayout';
import { FcGoogle } from 'react-icons/fc'; // Import Google icon
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react'; // Import eye icons for password visibility

interface LoginProps {
  email: string;
  onEmailChange: (email: string) => void;
  password: string;
  onPasswordChange: (password: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  onSignUpClick: () => void;
  onForgotPasswordClick: () => void;
  onGoogleSignIn?: () => Promise<void>; // Add Google sign-in handler
  error?: string;
}

export function Login({
  email,
  onEmailChange,
  password,
  onPasswordChange,
  onSubmit,
  onSignUpClick,
  onForgotPasswordClick,
  onGoogleSignIn,
  error,
}: LoginProps) {
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  return (
    <AuthLayout>
      <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 via-transparent to-purple-500/10 border border-white/10">
        <h2 className="text-3xl font-bold text-white mb-6 text-center">
          Welcome Back
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Input
              placeholder="Email address"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              required
              className="bg-black border-white/10 text-white placeholder:text-gray-400"
            />
          </div>
          <div className="relative">
            <Input
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              required
              className="bg-black border-white/10 text-white placeholder:text-gray-400 pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={onForgotPasswordClick}
              className="text-sm text-gray-400 hover:text-blue-400 transition-all duration-300"
            >
              Forgot password?
            </button>
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              className="flex-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 hover:text-blue-300 transition-all duration-300"
            >
              Sign in
            </Button>
            <Button
              type="button"
              onClick={onSignUpClick}
              className="flex-1 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 transition-all duration-300"
            >
              Register
            </Button>
          </div>

          {/* Add Google Sign In Button */}
          {onGoogleSignIn && (
            <>
              <div className="relative flex items-center py-2">
                <div className="flex-grow border-t border-gray-600"></div>
                <span className="flex-shrink mx-4 text-gray-400">or</span>
                <div className="flex-grow border-t border-gray-600"></div>
              </div>

              <Button
                type="button"
                onClick={onGoogleSignIn}
                className="w-full bg-transparent hover:bg-white/10 text-white flex items-center justify-center gap-2 transition-all duration-300 relative group overflow-hidden"
              >
                <FcGoogle className="text-xl" />
                <span>Continue with Google</span>

                {/* Glow line animation */}
                <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-blue-500/40 via-purple-500/40 to-blue-500/40 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />

                {/* Subtle glow halo */}
                <div className="absolute inset-0 -z-10 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 blur-md" />
              </Button>

              {/* Add the disclaimer */}
              <p className="text-xs text-gray-500 text-center mt-2">
                By signing in with Google, you agree to receive email promotions
                by default. You can update your preferences in settings.
              </p>
            </>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}
        </form>
      </div>
    </AuthLayout>
  );
}
